/**
 * Month-by-month reserves and profitability, per country, with the payments
 * behind every number.
 *
 * WHY (Fan, 2026-09-03): "There is still no visibility on a month by month
 * basis and no visibility on profitability (50% of the cash after supplier
 * payment should go to profits and 50% remain as working capital). I cannot
 * see which payments you took into account, how you did the division. In
 * addition, you have like 10 countries, it's so messy. I want for today just:
 * Germany, UK, Ireland and France."
 *
 * So: four countries by default (the rest are one line, not ten), a month
 * column, the whole division on every row, and every month expands to the
 * individual payments that make it up.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { brosTaxReservesAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

export const FOCUS_COUNTRIES = ['DE', 'UK', 'IE', 'FR'];
const COUNTRY_LABELS = { DE: 'Germany', UK: 'United Kingdom', IE: 'Ireland', FR: 'France' };

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function money(value, currency) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">unknown</span>;
  return formatCurrency(num(value), currency);
}

function monthLabel(month) {
  if (!month || month === 'unknown') return 'undated';
  const [year, mon] = month.split('-');
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(mon)] ?? mon} ${year}`;
}

function PaymentTable({ rows, currency }) {
  return (
    <table className="w-full text-[11px] mt-1" data-testid="reserve-payment-rows">
      <thead className="text-muted-foreground">
        <tr>
          <th className="text-left font-medium py-1">Invoice</th>
          <th className="text-left font-medium py-1">Client</th>
          <th className="text-left font-medium py-1">Dated</th>
          <th className="text-right font-medium py-1">Cash in</th>
          <th className="text-right font-medium py-1">VAT</th>
          <th className="text-right font-medium py-1">Supplier</th>
          <th className="text-right font-medium py-1">After supplier</th>
          <th className="text-right font-medium py-1">Tax</th>
          <th className="text-right font-medium py-1">Profit 50%</th>
          <th className="text-right font-medium py-1">Working cap. 50%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.invoice}-${row.date}`} className="border-t border-muted">
            <td className="py-1 font-mono">{row.invoice}</td>
            <td className="py-1">{row.client}</td>
            <td className="py-1">
              {row.date || '\u2014'}
              {row.month_basis === 'invoice_due_date' && (
                <span className="text-[10px] text-amber-600 ml-1" title="No matched bank payment, so this sits in its invoice due month">
                  due date
                </span>
              )}
            </td>
            <td className="py-1 text-right">{money(row.cash_received, currency)}</td>
            <td className="py-1 text-right">{money(row.output_vat, currency)}</td>
            <td className="py-1 text-right">{money(row.supplier_cost, currency)}</td>
            <td className="py-1 text-right">{money(row.cash_after_supplier, currency)}</td>
            <td className="py-1 text-right">
              {money(row.tax, currency)}
              <span className="text-muted-foreground ml-1">({num(row.tax_rate_pct)}%)</span>
            </td>
            <td className="py-1 text-right font-medium">{money(row.profit_share, currency)}</td>
            <td className="py-1 text-right">{money(row.working_capital, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CountryDetail({ row }) {
  const [open, setOpen] = useState(false);
  const cur = row.currency;
  return (
    <div className="mb-1" data-testid={`reserve-country-detail-${row.country}-${row.month}`}>
      <button
        type="button"
        className="w-full text-left text-[11px] flex items-center gap-1 hover:text-foreground text-muted-foreground"
        onClick={() => setOpen((prior) => !prior)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="font-medium text-foreground">
          {COUNTRY_LABELS[row.country] ?? row.country}
        </span>
        <span>
          cash in {money(row.cash_received, cur)} &minus; supplier {money(row.supplier_cost, cur)}
          {' '}&minus; VAT {money(row.vat_payable, cur)} &minus; tax {money(row.tax, cur)}
          {' '}= {money(num(row.profit_before_tax) - num(row.tax), cur)} &rarr; profit{' '}
          {money(row.profit_share, cur)} / working capital {money(row.working_capital, cur)}
          {' '}({row.payments} payment{row.payments === 1 ? '' : 's'})
        </span>
      </button>
      {open && <PaymentTable rows={row.payment_rows ?? []} currency={cur} />}
    </div>
  );
}

/**
 * One row per month, VAT per country in its own column.
 *
 * WHY (Fan, 2026-09-03): "Can you make it one row please per month and then
 * show the VAT for all countries in different columns only. The current way
 * it's done confuses me."
 *
 * Currencies are never added together: each country column carries its own
 * currency (UK in GBP, the rest in EUR). The rest of the division - cash,
 * supplier cost, tax, the profit split and the individual payments - lives one
 * click down, per country, so the row stays a single clean line.
 */
function MonthRow({ month, rows, countries }) {
  const [open, setOpen] = useState(false);
  const byCountry = {};
  rows.forEach((row) => { byCountry[row.country] = row; });
  return (
    <>
      <tr
        className="border-b hover:bg-muted/40 cursor-pointer"
        onClick={() => setOpen((prior) => !prior)}
        data-testid={`reserve-month-${month}`}
      >
        <td className="py-1 pl-1">
          <span className="inline-flex items-center gap-1">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {monthLabel(month)}
          </span>
        </td>
        {countries.map((country) => {
          const row = byCountry[country];
          return (
            <td key={country} className="py-1 text-right">
              {row ? money(row.vat_payable, row.currency) : <span className="text-muted-foreground">&mdash;</span>}
            </td>
          );
        })}
        <td className="py-1 text-right text-muted-foreground">
          {rows.reduce((total, row) => total + num(row.payments), 0)}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/20 border-b">
          <td colSpan={countries.length + 2} className="px-3 py-2">
            {rows.map((row) => (
              <CountryDetail key={`${row.country}-${row.currency}`} row={row} />
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

export function TaxReserveCountryMonths({ year = new Date().getFullYear() }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await brosTaxReservesAPI.getCountryMonths({
        countries: showAll ? 'all' : FOCUS_COUNTRIES.join(','),
        // Fan, 2026-09-03: "I told you to ONLY include 2026. NEVER 2025".
        year,
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [showAll, year]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1 py-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading month-by-month reserves
      </p>
    );
  }
  if (error) {
    return <p className="text-xs text-red-600 py-2">{error}</p>;
  }

  const months = data?.months ?? [];
  const totals = data?.country_totals ?? [];
  const others = data?.other_countries ?? [];

  // Pivot to one row per month, countries as columns (Fan, 2026-09-03).
  const byMonth = {};
  months.forEach((row) => {
    (byMonth[row.month] = byMonth[row.month] ?? []).push(row);
  });
  const monthKeys = Object.keys(byMonth).sort();
  const present = [...new Set(months.map((row) => row.country))];
  const columnCountries = [
    ...FOCUS_COUNTRIES.filter((country) => present.includes(country)),
    ...present.filter((country) => !FOCUS_COUNTRIES.includes(country)).sort(),
  ];
  const currencyOf = {};
  months.forEach((row) => { currencyOf[row.country] = row.currency; });

  return (
    <div className="mb-4" data-testid="reserve-country-months">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {data?.year ?? year} &mdash; month by month, per country: reserves, profit,
          and the {num(data?.split_pct)}% profit / working-capital split
        </p>
        <button
          type="button"
          className="text-[11px] underline text-muted-foreground hover:text-foreground"
          onClick={() => setShowAll((prior) => !prior)}
          data-testid="reserve-country-toggle"
        >
          {showAll ? 'Show Germany, UK, Ireland, France only' : 'Show every country'}
        </button>
      </div>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left font-medium py-1">Month</th>
            {columnCountries.map((country) => (
              <th key={country} className="text-right font-medium py-1">
                {COUNTRY_LABELS[country] ?? country} VAT
                <span className="block text-[10px] font-normal">
                  {currencyOf[country] ?? ''}
                </span>
              </th>
            ))}
            <th className="text-right font-medium py-1">Payments</th>
          </tr>
        </thead>
        <tbody>
          {monthKeys.map((month) => (
            <MonthRow
              key={month}
              month={month}
              rows={byMonth[month]}
              countries={columnCountries}
            />
          ))}
          {monthKeys.length === 0 && (
            <tr>
              <td colSpan={columnCountries.length + 2} className="py-2 text-muted-foreground">
                No payments in these countries.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="border-t-2">
          <tr className="font-medium">
            <td className="py-1 pl-1">Total VAT</td>
            {columnCountries.map((country) => {
              const total = totals.find((row) => row.country === country);
              return (
                <td key={country} className="py-1 text-right">
                  {total ? money(total.vat_payable, total.currency) : <span className="text-muted-foreground">&mdash;</span>}
                </td>
              );
            })}
            <td className="py-1 text-right text-muted-foreground">
              {totals.reduce((sum, row) => sum + num(row.payments), 0)}
            </td>
          </tr>
          <tr className="text-muted-foreground">
            <td className="py-1 pl-1">Profit share ({num(data?.split_pct)}%)</td>
            {columnCountries.map((country) => {
              const total = totals.find((row) => row.country === country);
              return (
                <td key={country} className="py-1 text-right">
                  {total ? money(total.profit_share, total.currency) : <span>&mdash;</span>}
                </td>
              );
            })}
            <td />
          </tr>
        </tfoot>
      </table>
      {others.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="reserve-other-countries">
          Not listed ({others.length} other{others.length === 1 ? '' : 's'}):{' '}
          {others.map((o) => `${o.country} ${formatCurrency(num(o.cash_received), o.currency ?? 'EUR')}`).join(' \u00b7 ')}
          {' '}&mdash; still computed, use &ldquo;show every country&rdquo;.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground mt-1">
        {data?.basis}. Profit share is {num(data?.split_pct)}% of what is left after VAT,
        supplier cost, refundable deposits and tax; the rest stays as working capital.
        Expand a month to see each country's cash, supplier cost, tax and split,
        and one more click for the individual payments behind it.
      </p>
    </div>
  );
}

export default TaxReserveCountryMonths;
